import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FaSearch, FaCalendarCheck, FaShoppingBag } from "react-icons/fa";
import CustomerHeader from "../../components/CustomerHeader";
import CustomerFooter from "../../components/CustomerFooter";
import { APP_LOGO_URL } from "../../config/branding";
import "./LoginStyles.css";
import "./PremiumAuth.css";

function CustomerSignupPage() {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);
  const navigate = useNavigate();

  const handleSocialLogin = (provider) => {
    const API_URL =
      process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";
    const providerLower = provider.toLowerCase();

    // Open OAuth popup
    const width = 600;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const popup = window.open(
      `${API_URL}/api/auth/${providerLower}`,
      `${provider} Login`,
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`,
    );

    // Listen for OAuth callback
    const checkPopup = setInterval(() => {
      try {
        if (popup.closed) {
          clearInterval(checkPopup);
          return;
        }

        // Check if popup redirected to success URL
        const popupUrl = popup.location.href;
        if (popupUrl.includes("/auth/success")) {
          const url = new URL(popupUrl);
          const token = url.searchParams.get("token");

          if (token) {
            // Save token
            localStorage.setItem("ogestyle_token", token);

            // Close popup
            popup.close();
            clearInterval(checkPopup);

            // Redirect to customer dashboard
            navigate("/customer/dashboard");
          }
        } else if (popupUrl.includes("/auth/error")) {
          popup.close();
          clearInterval(checkPopup);
          setError(`${provider} registration failed. Please try again.`);
        }
      } catch (err) {
        // Cross-origin errors are expected until redirect
      }
    }, 500);

    // Timeout after 5 minutes
    setTimeout(() => {
      clearInterval(checkPopup);
      if (popup && !popup.closed) {
        popup.close();
      }
    }, 300000);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (formData.password.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    if (!agreeToTerms) {
      setError("Please agree to the Terms of Service and Privacy Policy");
      return;
    }

    if (!smsConsent) {
      setError("Please agree to receive SMS notifications");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/customer/register`,
        {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          password: formData.password,
          phone: formData.phone,
          smsConsent: smsConsent,
        },
      );

      // Registration successful
      // Redirect to verification or login page
      if (response.data.requiresVerification) {
        navigate(
          `/login?email=${encodeURIComponent(formData.email)}&notice=${encodeURIComponent("Account created. Please verify your email via the link we sent, then log in with OTP.")}`,
        );
      } else {
        // Auto-login
        if (response.data.token) {
          localStorage.setItem("ogestyle_token", response.data.token);
          localStorage.setItem("ogestyle_role", "R5");
          localStorage.setItem("ogestyle_roles", JSON.stringify(["R5"]));
          navigate("/customer/dashboard");
        } else {
          navigate(`/login?email=${encodeURIComponent(formData.email)}`);
        }
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Registration failed. Please try again.";

      if (errorMessage.includes("email") && errorMessage.includes("already")) {
        setError(
          "This email is already registered. Please login or use a different email.",
        );
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <CustomerHeader />
      <div className="login-page og-premium-auth" style={{ minHeight: "calc(100vh - 50px)" }}>
        {/* Left Panel - Branding */}
        <div className="login-branding">
          <div className="og-branding">
            <p className="og-eyebrow">For customers</p>
            <h2 className="og-brand-title">
              Book beauty &amp; wellness near you
            </h2>
            <p className="og-brand-sub">
              Find top-rated professionals, book in seconds, and shop their
              products — no app to download, everything in one calm, connected
              place.
            </p>
            <div className="og-features">
              <div className="og-feature">
                <span className="og-feature-ic"><FaSearch /></span>
                <span>Find top-rated professionals instantly</span>
              </div>
              <div className="og-feature">
                <span className="og-feature-ic"><FaCalendarCheck /></span>
                <span>Book appointments 24/7 online</span>
              </div>
              <div className="og-feature">
                <span className="og-feature-ic"><FaShoppingBag /></span>
                <span>Shop beauty products from your pros</span>
              </div>
            </div>
            <div className="og-trust">
              <span>No app to download</span>
              <span>Free to join</span>
              <span>No card required</span>
            </div>
          </div>
        </div>

        {/* Right Panel - Form */}
        <div className="login-form-panel">
          <div className="login-container">
            <div className="login-header">
              <div className="mobile-logo"><img src={APP_LOGO_URL} alt="OGESTYLE" style={{ maxWidth: "120px", height: "auto" }} /></div>
              <h2>Create Customer Account</h2>
              <p className="subtitle">
                Join OGESTYLE to book beauty & wellness services
              </p>
            </div>

            {error && <div className="error-message">{error}</div>}

            <form onSubmit={handleSubmit}>
              <div
                className="form-row"
                style={{ display: "flex", gap: "16px" }}
              >
                <div className="form-group" style={{ flex: 1 }}>
                  <label htmlFor="firstName">First Name *</label>
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    placeholder="John"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group" style={{ flex: 1 }}>
                  <label htmlFor="lastName">Last Name *</label>
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    placeholder="Doe"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="john@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone Number *</label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="+1 (555) 123-4567"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* SMS Consent Checkbox (TCPA Compliance) */}
              <div
                style={{
                  marginTop: "24px",
                  padding: "20px",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "12px",
                  border: "1px solid #e9ecef",
                }}
              >
                <label
                  htmlFor="smsConsent"
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "12px",
                    cursor: "pointer",
                    fontSize: "14px",
                    lineHeight: "1.6",
                    color: "#1f2937",
                  }}
                >
                  <input
                    type="checkbox"
                    id="smsConsent"
                    checked={smsConsent}
                    onChange={(e) => setSmsConsent(e.target.checked)}
                    required
                    style={{
                      marginTop: "3px",
                      width: "18px",
                      height: "18px",
                      flexShrink: 0,
                      cursor: "pointer",
                      accentColor: "#1a659e",
                    }}
                  />
                  <span>
                    I agree to receive SMS notifications from OGESTYLE about my
                    appointments, including booking confirmations, reminders,
                    and updates. Message frequency may vary. Message and data
                    rates may apply. Text HELP for support. Reply STOP to
                    opt-out anytime.{" "}
                    <a
                      href="/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#1a659e", textDecoration: "underline" }}
                    >
                      Privacy Policy
                    </a>
                  </span>
                </label>
              </div>

              <div
                className="form-row"
                style={{ display: "flex", gap: "16px" }}
              >
                <div className="form-group" style={{ flex: 1 }}>
                  <label htmlFor="password">Password *</label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    placeholder="At least 8 characters"
                    value={formData.password}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group" style={{ flex: 1 }}>
                  <label htmlFor="confirmPassword">Confirm Password *</label>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    placeholder="Re-enter your password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div
                className="checkbox-group"
                style={{
                  marginTop: "20px",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "10px",
                }}
              >
                <input
                  type="checkbox"
                  id="agreeToTerms"
                  checked={agreeToTerms}
                  onChange={(e) => setAgreeToTerms(e.target.checked)}
                  required
                  style={{
                    marginTop: "3px",
                    width: "18px",
                    height: "18px",
                    flexShrink: 0,
                    cursor: "pointer",
                    accentColor: "#183a63",
                  }}
                />
                <label
                  htmlFor="agreeToTerms"
                  style={{
                    flex: 1,
                    minWidth: 0,
                    display: "block",
                    fontSize: "13.5px",
                    lineHeight: 1.65,
                    color: "#405b76",
                    cursor: "pointer",
                  }}
                >
                  I agree to the{" "}
                  <a
                    href="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#214b78", textDecoration: "underline" }}
                  >
                    Terms of Service
                  </a>{" "}
                  and{" "}
                  <a
                    href="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#214b78", textDecoration: "underline" }}
                  >
                    Privacy Policy
                  </a>
                  , and I&rsquo;d like to occasionally receive emails from
                  OGESTYLE.
                </label>
              </div>

              <button
                type="submit"
                className="login-button"
                disabled={loading}
                style={{ opacity: loading ? 0.6 : 1 }}
              >
                {loading ? "Creating Account..." : "Create Account"}
              </button>
            </form>

            {/* TEMPORARILY HIDDEN - Social Media Sign Up */}
            {/* <div className="divider">
            <span>or sign up with</span>
          </div>

          <div className="social-login-container">
            <button
              type="button"
              className="oauth-button google"
              onClick={() => handleSocialLogin("Google")}
            >
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              <span>Google</span>
            </button>
            <button
              type="button"
              className="oauth-button facebook"
              onClick={() => handleSocialLogin("Facebook")}
            >
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path
                  fill="#1877F2"
                  d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
                />
              </svg>
              <span>Facebook</span>
            </button>
            <button
              type="button"
              className="oauth-button apple"
              onClick={() => handleSocialLogin("Apple")}
            >
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"
                />
              </svg>
              <span>Apple</span>
            </button>
          </div> */}

            <div
              className="register-section"
              style={{ marginTop: "24px", textAlign: "center" }}
            >
              <p className="register-text">Already have an account?</p>
              <button
                className="link-button"
                onClick={() => navigate("/login")}
                style={{
                  background: "none",
                  border: "none",
                  color: "#1a659e",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
              >
                Log in here
              </button>
            </div>

            <div style={{ marginTop: "16px", textAlign: "center" }}>
              <p
                style={{ fontSize: "12px", color: "#6b7280", margin: "8px 0" }}
              >
                Are you a business owner or freelancer?
              </p>
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  justifyContent: "center",
                }}
              >
                <button
                  className="link-button"
                  onClick={() => navigate("/freelancer/signup")}
                  style={{
                    background: "#fff",
                    border: "1.5px solid #d8e2ec",
                    borderRadius: "999px",
                    padding: "9px 18px",
                    color: "#35516f",
                    fontSize: "13px",
                    fontWeight: "600",
                    cursor: "pointer",
                  }}
                >
                  Register as Freelancer
                </button>
                <button
                  className="link-button"
                  onClick={() => navigate("/register-business")}
                  style={{
                    background: "#fff",
                    border: "1.5px solid #d8e2ec",
                    borderRadius: "999px",
                    padding: "9px 18px",
                    color: "#35516f",
                    fontSize: "13px",
                    fontWeight: "600",
                    cursor: "pointer",
                  }}
                >
                  Register as Business
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <CustomerFooter />
    </>
  );
}

export default CustomerSignupPage;
